import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { connectSocket, onSocketEvent } from '../services/socket';
import { fetchCourseTasks } from '../redux/actions/tasks';
import { applyRemoteSubmission, applyTaskGrade, upsertTask } from '../redux/reducers/tasks';

/**
 * Sincroniza el módulo Tareas con el room del curso:
 * - created / updated → refresca el listado (el servidor aplica visibilidad)
 * - submitted → sube el conteo del profesor y avisa al detalle abierto
 */
export const useTaskUpdates = (courseId, { onTaskEvent = null } = {}) => {
    const dispatch = useDispatch();
    const role = useSelector((state) => state.tasks.role);
    const userId = useSelector((state) => state.user.user?._id);
    const roleRef = useRef(role);
    const userIdRef = useRef(userId);
    const onTaskEventRef = useRef(onTaskEvent);

    roleRef.current = role;
    userIdRef.current = userId;
    onTaskEventRef.current = onTaskEvent;

    useEffect(() => {
        if (!courseId) return undefined;

        let unsubs = [];
        let mounted = true;

        const setup = async () => {
            try {
                await connectSocket();
                if (!mounted) return;

                unsubs.push(onSocketEvent('task_update', (payload) => {
                    if (!mounted) return;
                    if (String(payload?.courseId) !== String(courseId)) return;

                    const action = payload.action;
                    const isSelf = payload.actorId
                        && String(payload.actorId) === String(userIdRef.current);

                    if (action === 'created' || action === 'updated') {
                        // El actor ya tiene el resultado del POST/PATCH en Redux;
                        // el resto refresca para respetar reglas de visibilidad.
                        if (!isSelf) {
                            dispatch(fetchCourseTasks(courseId));

                            if (
                                roleRef.current === 'student'
                                && action === 'created'
                                && payload.activationDate
                                && new Date(payload.activationDate).getTime() <= Date.now()
                            ) {
                                toast.success('Nueva tarea disponible', { icon: '📋' });
                            }
                        } else if (payload.task) {
                            dispatch(upsertTask(payload.task));
                        }
                    }

                    if (action === 'submitted') {
                        if (!isSelf && roleRef.current === 'teacher') {
                            dispatch(applyRemoteSubmission({
                                taskId: payload.taskId,
                                submittedCount: payload.submittedCount,
                            }));
                        }
                        // El alumno que entregó ya actualizó su mySubmission vía la acción HTTP.
                    }

                    if (action === 'graded') {
                        const isOwnerStudent = payload.studentId
                            && String(payload.studentId) === String(userIdRef.current);
                        if (isOwnerStudent && payload.submission) {
                            dispatch(applyTaskGrade({
                                taskId: payload.taskId,
                                submission: payload.submission,
                                gradedCount: payload.gradedCount,
                            }));
                            if (!isSelf) {
                                toast.success('Tu tarea fue calificada');
                            }
                        } else if (
                            typeof payload.gradedCount === 'number'
                            && (roleRef.current === 'teacher' || roleRef.current === 'observer')
                        ) {
                            dispatch(applyTaskGrade({
                                taskId: payload.taskId,
                                gradedCount: payload.gradedCount,
                            }));
                        }
                    }

                    onTaskEventRef.current?.(payload);
                }));
            } catch (err) {
                console.warn('[Tareas] Socket no disponible para actualizaciones:', err.message);
            }
        };

        setup();

        return () => {
            mounted = false;
            unsubs.forEach((fn) => {
                try {
                    fn();
                } catch {
                    // el socket pudo haberse cerrado al cambiar de curso
                }
            });
            unsubs = [];
        };
    }, [courseId, dispatch]);
};
